import _ from 'lodash'

import VALUES from 'core/data-model/constants/values'
import { isNil } from 'core/service/lib/type-check'

export const STANDARD_OCCUPATION = 0
export const INVALID_OCCUPATION = -1

export const transformOccupationFactor = (basicFactor) => {
  switch (basicFactor) {
    case 'std':
      return STANDARD_OCCUPATION
    case 'N':
      return INVALID_OCCUPATION
    default: {
      return _.toFinite(basicFactor)
    }
  }
}

/**
 * createInsuredOccupationService
 * @param dataWrapper {ReturnType<import('core/data/data-wrapper')._DB>}
 * @param displayProductService {ReturnType<import('core/service/display-product/display-product').createDisplayProductService>}
 */
export function createInsuredOccupationService(dataWrapper, displayProductService) {
  const _isExceptOccupationFactor = (code) => {
    const exceptProductCodes = [VALUES.IGROW, VALUES.IRETIRE_1, VALUES.IRETIRE_5]
    return _.includes(exceptProductCodes, code)
  }

  const _getOccupationFactor = (basicPlanCode, natureOfDuty, occupationFactors) => {
    const filteredOccupationFactors = occupationFactors.filter(
      ({ natureOfDutyCode }) => natureOfDuty === natureOfDutyCode
    )
    let occupationFactor = filteredOccupationFactors.find((oc) => basicPlanCode === oc.basicPlanCode)
    if (!occupationFactor) {
      occupationFactor = filteredOccupationFactors.find(
        ({ basicPlanCode, riderCode }) => isNil(basicPlanCode) && isNil(riderCode)
      )
    }

    return occupationFactor && !_isExceptOccupationFactor(basicPlanCode)
      ? transformOccupationFactor(occupationFactor.factor)
      : transformOccupationFactor('std')
  }

  const getOccupationFactor = async (displayProductQuery, natureOfDuty) => {
    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)
    return dataWrapper
      .getOccupationFactors()
      .then((occupationFactors) => _getOccupationFactor(basicPlan.productCode, natureOfDuty, occupationFactors))
  }

  const chooseMaxNumber = (factor, otherFactor) => {
    if (factor === INVALID_OCCUPATION || otherFactor === INVALID_OCCUPATION) {
      return INVALID_OCCUPATION
    }
    const _factor = _.isNumber(factor) ? factor : -Infinity

    return Math.max(_factor, otherFactor)
  }

  const _getRiskiestOccupation = (basicPlanCode, natureOfDuties, occupationFactors) => {
    const getOccupationFactorsByNatureOfDuty = (natureOfDuty) =>
      _getOccupationFactor(basicPlanCode, natureOfDuty, occupationFactors)

    return natureOfDuties.map(getOccupationFactorsByNatureOfDuty).reduce(chooseMaxNumber)
  }

  const getRiskiestOccupation = async (displayProductQuery, natureOfDuties) => {
    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)
    return dataWrapper
      .getOccupationFactors()
      .then((occupationFactors) => _getRiskiestOccupation(basicPlan.productCode, natureOfDuties, occupationFactors))
  }

  const _getOccupationFactorByValidOccupations = (
    basicPlanCode,
    natureOfDuty,
    occupationFactors,
    validOccupationFactors
  ) => {
    const occupationFactorForProduct = _getOccupationFactor(basicPlanCode, natureOfDuty, occupationFactors)
    return validOccupationFactors.map(transformOccupationFactor).includes(occupationFactorForProduct)
      ? occupationFactorForProduct
      : transformOccupationFactor('N')
  }

  const getOccupationFactorByValidOccupations = async (displayProductQuery, natureOfDuty) => {
    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)
    const validOccupationFactors = basicPlan.validOccupationFactors ? basicPlan.validOccupationFactors : []
    return dataWrapper
      .getOccupationFactors()
      .then((occupationFactors) =>
        _getOccupationFactorByValidOccupations(
          basicPlan.productCode,
          natureOfDuty,
          occupationFactors,
          validOccupationFactors
        )
      )
  }

  const getRiskiestOccupationFactorForRiders = async (displayProductQuery, natureOfDuties, riders) => {
    if (_.isEmpty(natureOfDuties) || _.isEmpty(riders)) {
      return Promise.resolve({})
    }

    const basicPlan = await displayProductService.getBasicPlan(displayProductQuery)
    return dataWrapper.getOccupationFactors().then((occupationFactors) =>
      _getRiskiestOccupationFactorForRiders(
        basicPlan.productCode,
        natureOfDuties,
        riders.map(({ code }) => ({ code })),
        occupationFactors
      )
    )
  }

  const getRiderOccupationFactor = (basicPlanCode, riderCode, occupationFactorsByNatureOfDuty) => {
    const isBasicPlanAndRiderCombination = (occupationFactor) =>
      basicPlanCode === occupationFactor.basicPlanCode && riderCode === occupationFactor.riderCode

    const isRiderOnly = (occupationFactor) =>
      isNil(occupationFactor.basicPlanCode) && riderCode === occupationFactor.riderCode

    const occupationFactorFromCombination = occupationFactorsByNatureOfDuty.find(isBasicPlanAndRiderCombination)

    const occupationFactorFromRiderOnly = occupationFactorsByNatureOfDuty.find(isRiderOnly)

    const occupationFactor = occupationFactorFromCombination || occupationFactorFromRiderOnly

    return occupationFactor ? transformOccupationFactor(occupationFactor.factor) : transformOccupationFactor('std')
  }

  const accumulateRiderOccupationFactor = (basicPlanCode, occupationFactorsByNatureOfDuty) => (acc, rider) => ({
    ...acc,
    [rider.code]: getRiderOccupationFactor(basicPlanCode, rider.code, occupationFactorsByNatureOfDuty),
  })

  const _getOccupationFactorForRiders = (basicPlanCode, natureOfDuty, riders, occupationFactors) => {
    const occupationFactorsByNatureOfDuty = occupationFactors.filter(
      ({ natureOfDutyCode }) => natureOfDuty === natureOfDutyCode
    )

    const emptyRiderOccupationFactors = {}

    return riders.reduce(
      accumulateRiderOccupationFactor(basicPlanCode, occupationFactorsByNatureOfDuty),
      emptyRiderOccupationFactors
    )
  }

  const _getRiskiestOccupationFactorForRiders = (basicPlanCode, natureOfDuties, riders, occupationFactors) => {
    const occupationFactorForRiders = (natureOfDuties || []).map((natureOfDuty) =>
      _getOccupationFactorForRiders(basicPlanCode, natureOfDuty, riders, occupationFactors)
    )

    const maxRisk = (ridersOccupationFactor, other) => _.mergeWith(ridersOccupationFactor, other, chooseMaxNumber)

    return occupationFactorForRiders.reduce(maxRisk, {})
  }

  return {
    _isExceptOccupationFactor,
    _getOccupationFactor,
    getOccupationFactor,
    _getRiskiestOccupation,
    getRiskiestOccupation,
    _getOccupationFactorByValidOccupations,
    getOccupationFactorByValidOccupations,
    getRiskiestOccupationFactorForRiders,
    _getOccupationFactorForRiders,
    _getRiskiestOccupationFactorForRiders,
  }
}
